import React, { useState, useEffect, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  Download,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from "lucide-react";
import Layout from "./layout";
import { examineeGroupData } from "../data/examineeGroupMockData";
import ExamineeGroupModal from "../Modal/ExamineeGroupModal";
import EditMemberModal from "../Modal/EditMemberModal";
import GroupingModal from "../Modal/GroupingModal";
import { campaignAPI, memberAPI, healthRecordsAPI } from "../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: 'Noto Sans Thai', sans-serif;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background-color: #00b5b8;
    border-bottom: none;
    padding-top: 0.75rem;
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .react-datepicker__current-month {
    color: white;
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: white;
    font-size: 0.75rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.15rem;
  }

  .react-datepicker__day {
    width: 2rem;
    line-height: 2rem;
    margin: 0.15rem;
    font-size: 0.75rem;
    color: #374151;
    border-radius: 0.25rem;
    transition: all 0.2s;

    &:hover {
      background-color: #e0f7f8;
      color: #00b5b8;
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00b5b8;
    color: white;
    font-weight: 500;

    &:hover {
      background-color: #0891b2;
    }
  }

  .react-datepicker__day--disabled {
    color: #d1d5db;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }

  .react-datepicker__day--today {
    font-weight: 600;
    color: #00b5b8;
    background-color: #e0f7f8;

    &:hover {
      background-color: #b3e5e8;
    }
  }

  .react-datepicker__day--outside-month {
    color: #d1d5db;
  }

  .react-datepicker__navigation {
    top: 0.75rem;
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const PageContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
`;

const UploadAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const UploadArea = styled.div`
  width: 100%;
  max-width: 500px;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;

  &:hover {
    border-color: #00b5b8;
    background-color: #e0f7f8;
    transform: scale(1.02);
  }
`;

const UploadText = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
`;

const UploadInput = styled.input`
  display: none;
`;

const DownloadExampleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00b5b8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;

  &:hover {
    color: #0891b2;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: #00b5b8;
  cursor: pointer;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DateInputContainer = styled.div`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const AddGroupButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;

const ResetButton = styled.button`
  background-color: #f9fafb;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const SearchButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #333333;
  }
`;

const GroupingButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #0891b2;
  }
`;

const RightButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00b5b8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  &:hover {
    color: #0891b2;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th`
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background-color: #f9fafb;
  white-space: nowrap;
  min-width: ${(props) => props.minWidth || "auto"};
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  font-size: 12px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  text-align: center;
  min-width: ${(props) => props.minWidth || "auto"};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: #374151;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${(props) => (props.active ? "#1976D2" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#1976D2")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${(props) => (props.active ? "none" : "underline")};

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.active ? "#1565C0" : "#E3F2FD")};
  }

  &:disabled {
    color: #9e9e9e;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const PaginationEllipsis = styled.span`
  padding: 0.5rem;
  color: #9e9e9e;
  font-size: 0.875rem;
`;

const _fmtDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const _today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
const _7dAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 7); d.setHours(0,0,0,0); return d; })();
const ExamineeGroup = () => {
  const [searchFilters, setSearchFilters] = useState({
    keyword: "",
    branch: "",
    startDate: _7dAgo,
    endDate: _today,
  });

  // ค่าที่ใช้ filter จริง — เริ่มต้น default 14 ส.ค. 2568
  const [activeFilters, setActiveFilters] = useState({
    keyword: "",
    branch: "",
    startDate: _fmtDate(_7dAgo),
    endDate: _fmtDate(_today),
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminStates, setAdminStates] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGroupingModalOpen, setIsGroupingModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [companies, setCompanies] = useState([]);
  const fetchIdRef = useRef(0);
  const currentFiltersRef = useRef({});
  const getActiveApiFilters = () => {
    const currentFilters = currentFiltersRef.current;
    if (currentFilters && Object.keys(currentFilters).length > 0) {
      return currentFilters;
    }

    const keywordTrimmed = (activeFilters.keyword || "").trim();
    const branchTrimmed = (activeFilters.branch || "").trim();

    return {
      dateStart: activeFilters.startDate,
      dateStop: activeFilters.endDate,
      ...(keywordTrimmed && { keywordName: keywordTrimmed }),
      ...(branchTrimmed && { keywordCampaign: branchTrimmed }),
    };
  };

  const reloadMembersWithCurrentFilters = (page = currentPage) => {
    fetchMembers(getActiveApiFilters(), { pageNow: page });
  };

  useEffect(() => {
    fetchMembers({
      dateStart: _fmtDate(_7dAgo),
      dateStop: _fmtDate(_today),
    });
    fetchCompanies();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const fetchCompanies = async () => {
    try {
      const firstResponse = await campaignAPI.getList(1);
      if (!firstResponse?.result || !Array.isArray(firstResponse.list)) {
        setCompanies([]);
        return;
      }

      const allCompanies = [...firstResponse.list];
      const totalPages = parseInt(firstResponse.pageAmount, 10) || 1;

      if (totalPages > 1) {
        const pageResponses = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            campaignAPI.getList(index + 2),
          ),
        );

        pageResponses.forEach((response) => {
          if (response?.result && Array.isArray(response.list)) {
            allCompanies.push(...response.list);
          }
        });
      }

      const uniqueCompanies = allCompanies.filter(
        (company, index, self) =>
          company?.name &&
          company.name !== "-" &&
          index === self.findIndex((item) => item.name === company.name),
      );

      setCompanies(uniqueCompanies);
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  };

  const fetchMembers = async (apiFilters = {}, { pageNow = 1 } = {}) => {
    const currentFetchId = ++fetchIdRef.current;

    setLoading(true);
    setError(null);
    currentFiltersRef.current = apiFilters;

    const formatList = (list) =>
      list.map((member) => ({
        ...member,
        campaign: member.memberCompany || "ทั่วไป",
      }));

    try {
      const params = { keywordName: "", ...apiFilters };

      const res = await memberAPI.getList({ pageNow, ...params });
      if (currentFetchId !== fetchIdRef.current) return;

      if (!Array.isArray(res.list)) {
        console.warn("API response format unknown");
        setMembers([]);
        setLoading(false);
        return;
      }

      const apiTotalPages = parseInt(res.pageAmount) || 1;
      setTotalPages(apiTotalPages);
      if (pageNow === 1 && res.list.length > 0) {
        setPageSize(res.list.length);
      }

      const data = formatList(res.list);
      setMembers([...data]);
      setAdminStates(
        data.reduce((acc, item) => {
          acc[item.id] = item.level === "2";
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Error fetching members:", err);
      setError(err.message);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const paginatedData = members;

  const handleFilterChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    const newFilters = {
      keyword: searchFilters.keyword,
      branch: searchFilters.branch,
      startDate: _fmtDate(searchFilters.startDate),
      endDate: _fmtDate(searchFilters.endDate),
    };
    setActiveFilters(newFilters);
    setCurrentPage(1);
    const apiFilters = {
      dateStart: newFilters.startDate,
      dateStop: _fmtDate(searchFilters.endDate),
      ...(searchFilters.keyword.trim() && { keywordName: searchFilters.keyword.trim() }),
      ...(searchFilters.branch.trim() && { keywordCampaign: searchFilters.branch.trim() }),
    };
    fetchMembers(apiFilters, { pageNow: 1 });
  };

  const handleReset = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    setSearchFilters({ keyword: "", branch: "", startDate: _7dAgo, endDate: today });
    setActiveFilters({ keyword: "", branch: "", startDate: _fmtDate(_7dAgo), endDate: _fmtDate(today) });
    setCurrentPage(1);
    fetchMembers({ dateStart: _fmtDate(_7dAgo), dateStop: _fmtDate(today) }, { pageNow: 1 });
  };

  const handleDownloadCSV = () => {
    const getGender = (gender) => {
      const genderStr = String(gender ?? "").trim();
      if (
        genderStr === "1" ||
        genderStr.toLowerCase() === "male" ||
        genderStr === "ชาย"
      )
        return "ชาย";
      if (
        genderStr === "0" ||
        genderStr.toLowerCase() === "female" ||
        genderStr === "หญิง"
      )
        return "หญิง";
      if (genderStr === "") return "-";
      return "อื่นๆ";
    };

    const calculateAge = (birthday) => {
      if (!birthday) return "-";
      let birthDate = new Date(birthday);
      if (isNaN(birthDate.getTime())) {
        return "-";
      }

      const today = new Date();
      let birthYear = birthDate.getFullYear();

      if (birthYear >= 2500) {
        birthYear = birthYear - 543;
        birthDate = new Date(
          birthYear,
          birthDate.getMonth(),
          birthDate.getDate(),
        );
      }

      let age = today.getFullYear() - birthYear;
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      if (age < 0 || age > 120) {
        return "-";
      }

      return age;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "-";
      const date = dateString.split(" ")[0];
      const parts = date.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return date;
    };

    const headers = [
      "ลำดับ",
      "วันที่สมัคร",
      "เลขบัตรประชาชน",
      "ชื่อ-สกุล",
      "บริษัท",
      "แผนก",
      "ตำแหน่ง",
    ];

    const dataToExport = members;

    const rows = dataToExport.map((member, index) => {
      const rowNumber = index + 1;
      const registrationDate = formatDate(
        member.mddateadd || member.registrationDate || member.dateadd,
      );
      const nid = member.nid ? `="${member.nid}"` : "-"; // ="..." บังคับ Excel แสดงเป็น text ป้องกัน scientific notation
      const fullName = member.name
        ? `${member.nameprefix || ""} ${member.name} ${member.lastname || ""}`.trim()
        : "-";
      const company = member.campaign || "-";
      const department = member.position || "-";
      const position = member.job || "-";

      return [
        rowNumber,
        registrationDate,
        nid,
        fullName,
        company,
        department,
        position,
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell);
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      )
      .join("\n");

    const BOM = "\uFEFF"; // BOM สำหรับ UTF-8 เพื่อให้ Excel เปิดภาษาไทยได้ถูกต้อง
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `รายชื่อสมาชิก_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExample = () => {
    console.log("ดาวน์โหลดตัวอย่าง v1");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      console.log("อัปโหลดไฟล์:", file.name);
    }
  };

  const handleUploadClick = () => {
    document.getElementById("file-upload").click();
  };

  const handleAdminToggle = async (id) => {
    const newState = !adminStates[id];

    setAdminStates((prev) => ({
      ...prev,
      [id]: newState,
    }));

    try {
      const member = members.find((m) => m.id === id);
      if (member) {
        await memberAPI.update({
          ...member,
          level: newState ? "2" : "1",
        });
      }
    } catch (err) {
      console.error("Error updating admin status:", err);
      setAdminStates((prev) => ({
        ...prev,
        [id]: !newState,
      }));
      alert("เกิดข้อผิดพลาดในการอัพเดทสถานะ: " + err.message);
    }
  };

  const handleSaveMember = async (memberData) => {
    try {
      if (memberData.id) {
        await memberAPI.update(memberData);
      } else {
        await memberAPI.add(memberData);
      }
      reloadMembersWithCurrentFilters(currentPage);
      setIsModalOpen(false);
      setEditingMember(null);
    } catch (err) {
      console.error("Error saving member:", err);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + err.message);
    }
  };

  const handleUpdateMember = async (memberData) => {
    try {
      const response = await memberAPI.update(memberData);

      if (response.result) {
        const selectedCompany = companies.find(
          (c) => String(c.id) === String(memberData.campaignid),
        );
        const companyName = selectedCompany?.name || "";
        const memberId =
          editingMember?.memberid || editingMember?.id || memberData.id;

        // Optimistic update — อัปเดต members state ทันที ไม่รอ fetchMembers
        setMembers((prev) =>
          prev.map((m) => {
            if (m.id !== memberData.id) return m;
            return {
              ...m,
              nameprefix: memberData.nameprefix,
              name: memberData.name,
              lastname: memberData.lastname,
              email: memberData.email,
              tel: memberData.tel,
              nid: memberData.nid,
              birthday: memberData.birthday,
              gender: memberData.gender,
              lineuid: memberData.lineuid,
              level: memberData.level,
              position: memberData.position,
              job: memberData.job,
              username: memberData.username,
              memberCompany: companyName,
              campaign: companyName || "ทั่วไป",
            };
          }),
        );
        setAdminStates((prev) => ({
          ...prev,
          [memberData.id]: memberData.level === "2",
        }));

        setIsEditModalOpen(false);
        setEditingMember(null);
        alert("บันทึกข้อมูลสำเร็จ");

        // เก็บชื่อบริษัทที่อัพเดตลง localStorage เพื่อให้หน้าสมุดสุขภาพอ่านได้
        if (companyName && memberId) {
          const updatedCompanies = JSON.parse(
            localStorage.getItem("updatedMemberCompanies") || "{}",
          );
          updatedCompanies[memberId] = companyName;
          localStorage.setItem(
            "updatedMemberCompanies",
            JSON.stringify(updatedCompanies),
          );
        }

        // refresh รายการ + updateCompanyByHn ใน background (fire-and-forget)
        const filtersForReload = getActiveApiFilters();
        const backgroundTasks = [
          fetchMembers(filtersForReload, { pageNow: currentPage }),
        ];
        if (companyName && memberId) {
          backgroundTasks.push(
            healthRecordsAPI.updateCompanyByHn(
              memberId,
              companyName,
              activeFilters.startDate || null,
              activeFilters.endDate || null,
            ).catch(() => {}),
          );
        }
        Promise.all(backgroundTasks);
      } else {
        const errorMsg =
          response.responseText || "เกิดข้อผิดพลาดในการอัพเดทข้อมูล";
        console.error("API returned error:", errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("Error updating member:", err);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + err.message);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingMember(null);
  };

  const handleSaveGrouping = async (groupingData) => {
    try {
      alert("จัดกลุ่มผู้รับการตรวจสำเร็จ");
      reloadMembersWithCurrentFilters(currentPage);
    } catch (err) {
      console.error("Error grouping members:", err);
      alert("เกิดข้อผิดพลาดในการจัดกลุ่ม: " + err.message);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchMembers(currentFiltersRef.current, { pageNow: page });
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        {loading && (
          <div
            style={{ textAlign: "center", padding: "1rem", fontSize: "12px" }}
          >
            กำลังโหลดข้อมูล...
          </div>
        )}
        {error && (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "#EF4444",
              fontSize: "12px",
            }}
          >
            เกิดข้อผิดพลาด: {error}
          </div>
        )}

        <FilterSection style={{ marginBottom: "1rem" }}>
          <FilterGroup style={{ flex: "1", minWidth: "250px" }}>
            <Label>ค้นหาจาก ชื่อ-สกุล หรือ เลขบัตร</Label>
            <TextInput
              type="text"
              value={searchFilters.keyword}
              onChange={(e) => handleFilterChange("keyword", e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </FilterGroup>

          <FilterGroup style={{ flex: "1", minWidth: "200px" }}>
            <Label>บริษัท</Label>
            <SelectInput
              value={searchFilters.branch}
              onChange={(e) => handleFilterChange("branch", e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              {companies
                .filter((company) => company.name && company.name !== "-")
                .map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
            </SelectInput>
          </FilterGroup>

          <FilterGroup style={{ flex: "1", minWidth: "180px" }}>
            <Label>วันที่เริ่มต้น</Label>
            <DateInputContainer>
              <DatePicker
                selected={searchFilters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </FilterGroup>

          <FilterGroup style={{ flex: "1", minWidth: "180px" }}>
            <Label>ถึงวันที่</Label>
            <DateInputContainer>
              <DatePicker
                selected={searchFilters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={searchFilters.startDate}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </FilterGroup>

          <SearchButton onClick={handleSearch}>ค้นหา</SearchButton>
        </FilterSection>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            marginBottom: "1.5rem",
          }}
        >
          <DownloadButton onClick={handleDownloadCSV}>
            <Download size={16} />
            ดาวน์โหลด CSV
          </DownloadButton>

          <GroupingButton onClick={() => setIsGroupingModalOpen(true)}>
            จัดกลุ่มผู้รับการตรวจ
          </GroupingButton>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <RightButtonGroup>
            <PaginationContainer style={{ marginTop: "0.5rem" }}>
              <PaginationButton
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                previous
              </PaginationButton>
              {(() => {
                const pages = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(
                      <PaginationButton
                        key={i}
                        active={currentPage === i}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </PaginationButton>,
                    );
                  }
                } else {
                  pages.push(
                    <PaginationButton
                      key={1}
                      active={currentPage === 1}
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </PaginationButton>,
                  );
                  if (currentPage > 3) {
                    pages.push(<PaginationEllipsis key="e1">...</PaginationEllipsis>);
                  }
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <PaginationButton
                        key={i}
                        active={currentPage === i}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </PaginationButton>,
                    );
                  }
                  if (currentPage < totalPages - 2) {
                    pages.push(<PaginationEllipsis key="e2">...</PaginationEllipsis>);
                  }
                  pages.push(
                    <PaginationButton
                      key={totalPages}
                      active={currentPage === totalPages}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </PaginationButton>,
                  );
                }
                return pages;
              })()}
              <PaginationButton
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                next
              </PaginationButton>
            </PaginationContainer>
          </RightButtonGroup>
        </div>
      </PageContainer>

      <div style={{ overflowX: "auto" }}>
        <Table>
          <thead>
            <tr>
              <TableHeader minWidth="60px">ที่</TableHeader>
              <TableHeader minWidth="120px">วันที่ตรวจ</TableHeader>
              <TableHeader minWidth="110px">เวลา</TableHeader>
              <TableHeader minWidth="140px">บัตรประชาชน</TableHeader>
              <TableHeader minWidth="150px">ชื่อ-สกุล</TableHeader>
              <TableHeader minWidth="120px">บริษัท</TableHeader>
              <TableHeader minWidth="100px">แผนก</TableHeader>
              <TableHeader minWidth="100px">ตำแหน่ง</TableHeader>
              <TableHeader minWidth="80px">admin</TableHeader>
              <TableHeader minWidth="80px">แก้ไข</TableHeader>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={10}
                  style={{
                    textAlign: "center",
                    padding: "3rem 1rem",
                    color: "#9ca3af",
                    fontSize: "14px",
                  }}
                >
                  ไม่พบรายการ
                </td>
              </tr>
            )}
            {paginatedData.map((row, index) => {
              const formatDate = (dateString) => {
                if (!dateString) return "-";
                const date = dateString.split(" ")[0];
                const parts = date.split("-");
                if (parts.length === 3) {
                  return `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
                return date;
              };

              const getGender = (gender) => {
                if (gender === "1") return "ชาย";
                if (gender === "2") return "หญิง";
                return "อื่นๆ";
              };

              const rowNumber = (currentPage - 1) * pageSize + index + 1;

              return (
                <tr key={(currentPage - 1) * pageSize + index}>
                  <TableCell minWidth="60px">{rowNumber}</TableCell>
                  <TableCell minWidth="120px">
                    {formatDate(row.mddateadd || row.registrationDate || row.dateadd)}
                  </TableCell>
                  <TableCell minWidth="110px">
                    {(row.mddateadd || row.registrationDate || row.dateadd || "").split(" ")[1]?.slice(0, 8) || "-"}
                  </TableCell>
                  <TableCell minWidth="100px">{row.nid || "-"}</TableCell>
                  <TableCell minWidth="150px">
                    {row.name
                      ? `${row.nameprefix || ""} ${row.name} ${row.lastname || ""}`.trim()
                      : "-"}
                  </TableCell>
                  <TableCell minWidth="120px">{row.campaign || "-"}</TableCell>
                  <TableCell minWidth="100px">{row.position || "-"}</TableCell>
                  <TableCell minWidth="100px">{row.job || "-"}</TableCell>
                  <TableCell minWidth="80px">
                    <input
                      type="checkbox"
                      checked={adminStates[row.id]}
                      disabled={true}
                      style={{
                        cursor: "not-allowed",
                        transform: "scale(1.0)",
                      }}
                    />
                  </TableCell>
                  <TableCell minWidth="80px">
                    <EditButton onClick={() => handleEditMember(row)}>
                      <Edit2 size={12} />
                    </EditButton>
                  </TableCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <ExamineeGroupModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveMember}
        memberData={null}
      />

      <EditMemberModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleUpdateMember}
        memberData={editingMember}
      />

      <GroupingModal
        isOpen={isGroupingModalOpen}
        onClose={() => setIsGroupingModalOpen(false)}
        onSave={handleSaveGrouping}
      />
    </Layout>
  );
};

export default ExamineeGroup;
